import { useState } from 'react';
import { useBarcodeScan } from 'src/hooks/useBarcodeScan';
import usePDA1110_R01Store, { BarcodeInputItem } from './usePDA1110_R01Store';
import useUiuxStore from 'src/store/useUiuxStore';
import { ItemDto } from 'src/dtos/item/itemDto';
import { useToast } from 'src/hooks/useToast';

interface PDA1110_R01InfoProps {
  onBarcodeScan: (barcode: string) => Promise<boolean>;
  onSave: () => void;
}

const PDA1110_R01Info = ({ onBarcodeScan, onSave }: PDA1110_R01InfoProps) => {
  const { barcodeInputList, updateBarcodeInput, removeBarcodeInput } = usePDA1110_R01Store();
  const { setCodeSearchModalVisible, setCodeSearchModalProps } = useUiuxStore();
  const [scanValue, setScanValue] = useState('');
  const [keyboardEnabled, setKeyboardEnabled] = useState(false);
  const [lastTapTime, setLastTapTime] = useState(0);
  const { warning } = useToast();

  // 바코드 스캔 훅 사용
  const { scanInputRef, handleScanKeyDown, handleInput, clear } = useBarcodeScan(
    async (barcode: string) => {
      const normalizedBarcode = barcode.toUpperCase();
      await onBarcodeScan(normalizedBarcode);
      
      // 스캔 후 입력 필드 초기화
      clear();
      setScanValue('');
    },
    {
      clearOnScan: false,
      refocusOnScan: true,
      debounceMs: 300,
      autoFocus: true,
    }
  );

  // 더블탭으로 키보드 활성화
  const handleDoubleTap = () => {
    const now = Date.now();
    const timeDiff = now - lastTapTime;
    
    if (timeDiff < 300) {
      setKeyboardEnabled(true);
      scanInputRef.current?.focus();
    }
    
    setLastTapTime(now);
  };

  // 키보드 토글 버튼
  const toggleKeyboard = () => {
    setKeyboardEnabled(!keyboardEnabled);
    if (!keyboardEnabled) {
      setTimeout(() => scanInputRef.current?.focus(), 100);
    }
  };

  // 품목 검색 모달 열기
  const openItemSearchModal = (index: number) => {
    const item = barcodeInputList[index];
    if (!item) return;

    setCodeSearchModalProps({
      category: '품목',
      search: '', // 팝업을 열 때마다 검색어 초기화
      extraFilter: { hideFilters: true },
      autoSelectSingle: true,
      autoFocus: false,
      onConfirm: (selectedItem: ItemDto) => {
        // index를 직접 사용하여 업데이트
        updateBarcodeInput(index, {
          itemCd: selectedItem.itemCd,
          itemNm: selectedItem.itemNm,
          itemSpec: selectedItem.itemSpec,
          itemUnit: selectedItem.itemUnit,
          modelNo: selectedItem.modelNo,
        });
        setCodeSearchModalVisible(false);
      },
      onCloseConfirm: () => {
        setCodeSearchModalVisible(false);
      }
    });
    setCodeSearchModalVisible(true);
  };

  // 품목코드 입력 변경
  const handleItemCdChange = (index: number, value: string) => {
    updateBarcodeInput(index, { itemCd: value });
  };

  // 품목코드 더블클릭 또는 엔터키 처리
  const handleItemCdKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      openItemSearchModal(index);
    }
  };

  // 포장수량 변경
  const handlePackQtyChange = (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseFloat(e.target.value) || 1;
    updateBarcodeInput(index, { packQty: value });
  };

  // 바코드 삭제
  const handleRemoveBarcode = (index: number) => {
    removeBarcodeInput(index);
  };

  return (
    <div className="w-full md:w-45 bg-white p-4 pb-1 border-r border-gray-200 overflow-y-auto">
      <div className="space-y-1">
        {/* 바코드 스캔 입력 */}
        <div className="flex items-center gap-1">
          <label className="text-lg font-medium text-black w-20 flex-shrink-0">스캔</label>
          <div className="flex-1 flex gap-1">
            <input
              ref={scanInputRef}
              type="text"
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onInput={handleInput}
              onKeyDown={handleScanKeyDown}
              onClick={handleDoubleTap}
              inputMode={keyboardEnabled ? "text" : "none"}
              autoComplete="off"
              className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="바코드 스캔"
            />
            <button
              type="button"
              onClick={toggleKeyboard}
              className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded transition-colors ${
                keyboardEnabled 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
              title={keyboardEnabled ? "키보드 숨기기" : "키보드 표시"}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </button>
          </div>
        </div>

        {/* 바코드 입력 폼 */}
        <div className="space-y-3 border-t pt-3">
          {/* 항상 하나의 폼만 표시 (첫 번째 항목 또는 빈 폼) */}
          {(() => {
            const item = barcodeInputList.length > 0 ? barcodeInputList[0] : null;
            return (
            <div className="space-y-3">
              {/* 바코드 */}
              <div className="flex items-center gap-1">
                <label className="text-lg font-medium text-black w-20 flex-shrink-0">바코드</label>
                <div className="flex-1 flex gap-1">
                  <input
                    type="text"
                    value={item?.barcode || ''}
                    readOnly
                    className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
                    placeholder="바코드"
                  />
                  <div className="w-10 flex-shrink-0"></div>
                </div>
              </div>

              {/* 품목코드 */}
              <div className="flex items-center gap-1">
                <label className="text-lg font-medium text-black w-20 flex-shrink-0">품목코드</label>
                <div className="flex-1 flex gap-1">
                  <input
                    type="text"
                    value={item?.itemCd || ''}
                    onChange={(e) => handleItemCdChange(0, e.target.value)}
                    onDoubleClick={() => openItemSearchModal(0)}
                    onKeyDown={(e) => handleItemCdKeyDown(0, e)}
                    className={`flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded ${
                      item?.isEditable !== false
                        ? 'bg-white text-black cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500' 
                        : 'bg-gray-100 text-gray-600 cursor-not-allowed'
                    }`}
                    placeholder="품목코드"
                    readOnly={item?.isEditable === false}
                  />
                  <button
                    type="button"
                    onClick={() => openItemSearchModal(0)}
                    disabled={item?.isEditable === false}
                    className={`w-10 flex-shrink-0 px-2 border border-gray-300 rounded ${
                      item?.isEditable !== false
                        ? 'bg-blue-500 text-white hover:bg-blue-600 transition-colors'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    title="품목 검색"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* 품명 */}
              <div className="flex items-center gap-1">
                <label className="text-lg font-medium text-black w-20 flex-shrink-0">품명</label>
                <div className="flex-1 flex gap-1">
                  <input
                    type="text"
                    value={item?.itemNm || ''}
                    readOnly
                    className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
                    placeholder="품명"
                  />
                  <div className="w-10 flex-shrink-0"></div>
                </div>
              </div>

              {/* 규격 */}
              <div className="flex items-center gap-1">
                <label className="text-lg font-medium text-black w-20 flex-shrink-0">규격</label>
                <div className="flex-1 flex gap-1">
                  <input
                    type="text"
                    value={item?.itemSpec || ''}
                    readOnly
                    className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
                    placeholder="규격"
                  />
                  <div className="w-10 flex-shrink-0"></div>
                </div>
              </div>

              {/* 등번 */}
              <div className="flex items-center gap-1">
                <label className="text-lg font-medium text-black w-20 flex-shrink-0">품번</label>
                <div className="flex-1 flex gap-1">
                  <input
                    type="text"
                    value={item?.modelNo || ''}
                    readOnly
                    className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black cursor-not-allowed"
                    placeholder="품번"
                  />
                  <div className="w-10 flex-shrink-0"></div>
                </div>
              </div>

              {/* 포장량 */}
              <div className="flex items-center gap-1">
                <label className="text-lg font-medium text-black w-20 flex-shrink-0">포장량</label>
                <div className="flex-1 flex gap-1">
                  <input
                    type="number"
                    value={item?.packQty || 1}
                    onChange={(e) => handlePackQtyChange(0, e)}
                    className="flex-1 max-w-[173px] px-2 text-lg border border-gray-300 rounded bg-white text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="포장량"
                    min="1"
                    step="1"
                  />
                  <input
                    type="text"
                    value={item?.itemUnit || "ea"}
                    readOnly
                    className="w-10 flex-shrink-0 px-2 text-lg border border-gray-300 rounded bg-gray-100 text-black text-center cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
            );
          })()}
          
        </div>

        {/* 저장 버튼 */}
        <div className="pt-2 flex justify-center">
          <button
            type="button"
            onClick={onSave}
            className="px-8 py-2 bg-blue-500 text-white rounded text-lg font-medium hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDA1110_R01Info;
